import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ParticleBackground from "@/components/ParticleBackground";
import AIGrid from "@/components/AIGrid";
import { Suspense, lazy } from "react";
const InteractiveArchitectureDiagram = lazy(() => import("@/components/InteractiveArchitectureDiagram"));
const AnalyticsDashboardMockup = lazy(() => import("@/components/AnalyticsDashboardMockup"));
import FeatureComparisonTable from "@/components/FeatureComparisonTable";
import StatsCounter from "@/components/StatsCounter";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Zap, Lock, Code, Layers } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ZeroCodeIntegration from "@/components/platform/ZeroCodeIntegration";
import LatencyDemo from "@/components/platform/LatencyDemo";
import AIFrameworksDemo from "@/components/platform/AIFrameworksDemo";
import SecurityDemo from "@/components/platform/SecurityDemo";

const Platform = () => {
  const stats = [
    { value: "20", label: "AI Frameworks", suffix: "+" },
    { value: "99.9", label: "Uptime SLA", suffix: "%" },
    { value: "50", label: "Response Time", suffix: "ms" },
    { value: "∞", label: "Concurrent Calls", suffix: "" },
  ];

  const technicalDifferentiators = [
    {
      icon: Code,
      title: "Zero-Code Integration",
      description: "Plug-and-play with any CRM, no development needed. Connect Salesforce, HubSpot, SAP, or custom APIs in hours, not months.",
    },
    {
      icon: Zap,
      title: "Near-Zero Latency",
      description: "<50ms response time for human-like conversations. Advanced processing pipeline ensures natural flow without delays.",
    },
    {
      icon: Layers,
      title: "20+ AI Frameworks",
      description: "Custom-built ensemble architecture combining NLP, intent classification, sentiment analysis, and generative AI for unmatched accuracy.",
    },
    {
      icon: Lock,
      title: "Enterprise Security",
      description: "ISO 27001, GDPR, SOC 2 Type II certified. End-to-end encryption, role-based access, comprehensive audit trails.",
    },
  ];

  const renderVisualForFeature = (title: string) => {
    switch (title) {
      case "Zero-Code Integration":
        return <ZeroCodeIntegration />;
      case "Near-Zero Latency":
        return <LatencyDemo />;
      case "20+ AI Frameworks":
        return <AIFrameworksDemo />;
      case "Enterprise Security":
        return <SecurityDemo />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background relative overflow-hidden">
      <ParticleBackground />
      <AIGrid />
      <Navigation />
      
      <main className="flex-grow pt-32 pb-20 relative z-10">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-8 gradient-brand-text tracking-tight">Platform & Technology</h1>
            <p className="text-2xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
              Built from scratch with 20+ AI frameworks for enterprise-grade performance, security, and unlimited scale
            </p>
          </motion.div>

          <div className="mb-20">
            <StatsCounter stats={stats} />
          </div>

          <div className="my-32">
            <Suspense fallback={<div className="py-24 text-center text-muted-foreground">Loading architecture…</div>}>
              <InteractiveArchitectureDiagram />
            </Suspense>
          </div>

          <div className="my-40">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-5xl lg:text-6xl font-bold mb-20 text-center tracking-tight"
            >
              Technical <span className="gradient-brand-text">Differentiators</span>
            </motion.h2>
            <div className="space-y-12">
              {technicalDifferentiators.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: index * 0.15 }}
                  className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:flex-row-reverse' : ''}`}
                >
                  <div className={`${index % 2 === 1 ? 'md:order-2' : ''}`}>
                    <Card className="p-16 glass-effect border-primary/40 hover-lift group h-full">
                      <div className="relative w-28 h-28 rounded-3xl bg-gradient-to-br from-primary to-accent flex items-center justify-center mb-10 shadow-2xl glow-effect">
                        <feature.icon className="w-14 h-14 text-white" />
                        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-cyan-500/30 to-purple-500/30 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      </div>
                      <h3 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">{feature.title}</h3>
                      <p className="text-muted-foreground leading-relaxed text-xl">{feature.description}</p>
                    </Card>
                  </div>
                  <div className={`${index % 2 === 1 ? 'md:order-1' : ''}`}>
                    <Card className="p-12 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-accent/5">
                      <div className="aspect-video rounded-xl overflow-hidden">
                        {renderVisualForFeature(feature.title) || (
                          <div className="w-full h-full bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center border-2 border-primary/30">
                            <div className="text-center p-8">
                              <feature.icon className="w-20 h-20 text-primary mx-auto mb-4 opacity-50" />
                              <p className="text-sm text-muted-foreground">Visual coming soon</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="my-32">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight"
            >
              Feature <span className="gradient-brand-text">Comparison</span>
            </motion.h2>
            <FeatureComparisonTable />
          </div>

          <div className="mb-32">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-3xl md:text-4xl lg:text-5xl font-bold mb-16 text-center tracking-tight"
            >
              Analytics & <span className="gradient-brand-text">Insights Dashboard</span>
            </motion.h2>
            <Suspense fallback={<div className="py-16 text-center text-muted-foreground">Loading analytics…</div>}>
              <AnalyticsDashboardMockup />
            </Suspense>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Card className="p-16 glass-effect border-primary/30 bg-gradient-to-br from-primary/20 to-accent/20 hover-lift">
              <h2 className="text-3xl md:text-4xl font-bold mb-8 tracking-tight">Ready to See It in Action?</h2>
              <p className="text-xl text-muted-foreground mb-12 max-w-3xl mx-auto leading-relaxed">
                Request our comprehensive technical whitepaper or schedule a personalized platform demo
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90 text-white">
                    Schedule Platform Demo
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white">
                    Download Whitepaper
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Platform;
